import React from 'react'
import { Typography, Grid, Box, Container, useTheme } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import HomeIcon from '@mui/icons-material/Home'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import PeopleIcon from '@mui/icons-material/People'
import BrandedPageHeader from 'components/Layout/BrandedPageHeader'
import { useHousing } from './useHousing'
import { useFormControls } from './formActions'
import HousingForm from './HousingForm'
import { useSelector } from 'react-redux'

const services = [
  {
    icon: SearchIcon,
    title: 'Pre-Decision Services',
    description: 'Personalized needs assessment, tours of prospective move locations, community comparison, and cost of living insights.',
  },
  {
    icon: HomeIcon,
    title: 'Destination Services',
    description: 'Area orientation tours, home search assistance for purchase or lease, mortgage service recommendations, and settling-in support.',
  },
  {
    icon: LocalShippingIcon,
    title: 'Departure Services',
    description: 'Home sale assistance at your current location and move-out support.',
  },
  {
    icon: PeopleIcon,
    title: 'Additional Services',
    description: 'Moving and transportation recommendations, spouse and family support.',
  },
]

const Housing = () => {
  const { signedIn } = useHousing()
  const theme = useTheme()
  const { formData, formSubmitted, errors, handleSubmit, handleCreateAccount, handleChange } =
    useFormControls()

  const hasSubmittedHousingInterest = useSelector(state => Boolean(state.candidate?.housingInterest))

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden' }}>
      <BrandedPageHeader>
        <Grid container spacing={0} sx={{ px: 0 }}>
          <Grid item xs={12} md={8} sx={{ pl: 0 }}>
            <Typography variant='h5' sx={{ color: 'white', fontWeight: 'bold', mb: 1, pl: 0 }}>
              Relocation Assistance
            </Typography>
            <Typography variant='caption' sx={{
              color: 'rgba(255,255,255,0.7)',
              display: 'block',
              mb: { xs: 2, md: 0 },
              whiteSpace: 'normal',
              wordBreak: 'normal',
              pl: 0
            }}>
              {hasSubmittedHousingInterest
                ? "Thank you for your interest in our relocation services. Our team has received your request and will be in touch with you shortly."
                : "Get help with your relocation through our partnership with Spyglass Relocation Services and the LeadingRE network."}
            </Typography>
          </Grid>
        </Grid>
      </BrandedPageHeader>

      <Container sx={{ py: { xs: 2, md: 4 }, px: { xs: 2, md: 3 } }}>
        <Box
          sx={{
            backgroundColor: theme.palette.grey[50],
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            borderRadius: 1,
            p: { xs: 2, md: 3 },
            mb: { xs: 2, md: 4 },
          }}
        >
          <Typography variant='body1' sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
            MyStethi has partnered with{' '}
            <a href='https://www.spyglassrealty.com/' target='_blank' rel='noopener noreferrer'>Spyglass Relocation Services</a>
            {' '}to connect medical professionals with realtors and relocation services through the{' '}
            <a href='https://www.leadingre.com' target='_blank' rel='noopener noreferrer'>Leading Real Estate Companies of the World® (LeadingRE)</a>
            {' '}network. Members of the MyStethi community may be eligible for savings of up to $2,000 on closing costs.
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 2, md: 4 }} alignItems='flex-start'>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              {services.map(({ icon: Icon, title, description }) => (
                <Grid item xs={12} sm={6} key={title}>
                  <Box
                    sx={{
                      backgroundColor: 'white',
                      borderRadius: 2,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      p: { xs: 2, md: 3 },
                      height: '100%',
                    }}
                  >
                    <Icon sx={{ color: theme.palette.primary.dark, mb: 1 }} />
                    <Typography variant='h6' sx={{ color: theme.palette.primary.dark, mb: 1, fontSize: { xs: '1rem', md: '1.25rem' } }}>
                      {title}
                    </Typography>
                    <Typography variant='body2'>
                      {description}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            {hasSubmittedHousingInterest ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant='h5' sx={{ mb: 2, color: theme.palette.primary.dark }}>
                  Your Relocation Request is Being Processed
                </Typography>
                <Typography variant='body1'>
                  One of our relocation experts will be reaching out to you soon to discuss your potential move.
                  If you have any urgent questions, please don't hesitate to contact us.
                </Typography>
              </Box>
            ) : (
              <HousingForm
                formSubmitted={formSubmitted}
                handleSubmit={handleSubmit}
                formData={formData}
                handleChange={handleChange}
                errors={errors}
                handleCreateAccount={handleCreateAccount}
                signedIn={signedIn}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            <Typography
              variant='caption'
              sx={{ display: 'block', mt: 2, fontStyle: 'italic', color: theme.palette.text.secondary }}
            >
              MyStethi may receive a fee from referrals for relocation assistance.
            </Typography>
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}

export default Housing
